
import { Directive, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { parsePhoneNumber } from 'libphonenumber-js/min';
import { defer, merge } from 'rxjs';
import { debounceTime, map, scan, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AnalyticsFormService } from '@pe/checkout/analytics';
import { CompositeForm } from '@pe/checkout/forms';
import { AddressAutocompleteService, addressMask } from '@pe/checkout/forms/address-autocomplete';
import { emailRequiredValidator } from '@pe/checkout/forms/email';
import { namePatternValidator } from '@pe/checkout/forms/name';
import { PhoneValidators } from '@pe/checkout/forms/phone';
import { phoneMask } from '@pe/checkout/forms/phone';
import { POSTAL_PATTERNS_BY_COUNTRY, ZipCodeValidators } from '@pe/checkout/forms/zip-code';
import { FlowState } from '@pe/checkout/store';
import {
  AddressInterface,
  FlowInterface,
  PaymentAddressSettingsInterface,
} from '@pe/checkout/types';
import { AddressMapperService, CustomElementService } from '@pe/checkout/utils';

import {
  AddressValidators,
  SALUTATION_OPTIONS,
  SELECT_ADDRESS_OPTIONS,
} from '../../constants';
import { AddressFormInterface } from '../../interfaces';
import { AddressService } from '../../services';

const NOT_ONLY_WHITE_SPACES = Validators.pattern(/\S+/);


@Directive()
export abstract class AbstractAddressEditComponent extends CompositeForm<AddressFormInterface> implements OnInit {

  public formGroup: FormGroup = this.fb.group({
    email: this.fb.control<string>(null, emailRequiredValidator),
    salutation: this.fb.control<string>(null, [Validators.required]),
    firstName: this.fb.control<string>(null, [Validators.required, NOT_ONLY_WHITE_SPACES, namePatternValidator]),
    lastName: this.fb.control<string>(null, [Validators.required, NOT_ONLY_WHITE_SPACES, namePatternValidator]),
    country: this.fb.control<string>(null, [Validators.required]),
    city: this.fb.control<string>(null, [Validators.required, Validators.pattern(/^([^0-9]*)$/)]),
    fullAddress: this.fb.control<string>(null),
    street: this.fb.control<string>(null, [Validators.required, AddressValidators.houseNumberRequired]),
    streetName: this.fb.control<string>(null),
    streetNumber: this.fb.control<string>(null),
    socialSecurityNumber: this.fb.control<string>(null),
    zipCode: this.fb.control<string>(null, [ZipCodeValidators.Required]),
    phone: this.fb.control<string>(null),
  });

  public readonly phoneMask = phoneMask;
  public readonly addressMask = addressMask;

  private addressService = this.injector.get(AddressService);
  private analyticsFormService = this.injector.get(AnalyticsFormService);
  protected addressMapperService = this.injector.get(AddressMapperService);
  protected customElementService = this.injector.get(CustomElementService);
  protected addressAutocompleteService = this.injector.get(AddressAutocompleteService);

  @SelectSnapshot(FlowState.flow) flow!: FlowInterface;

  @Input() paymentSettings: PaymentAddressSettingsInterface;

  @Input() isFilledFieldsReadonly = true;

  @Input() isPhoneRequired: boolean;

  @Input() isCodeForPhoneRequired: boolean;


  public readonly addressItems$ = this.addressAutocompleteService.addressItems$;
  public readonly salutationFormOptions = SALUTATION_OPTIONS;
  public readonly selectAddressFormOptions = [
    ...SELECT_ADDRESS_OPTIONS,
    ...(this.flow.shippingAddresses ?? []).map(value => ({ label: value.fullAddress, value })),
  ];

  public readonly countriesOptions$ = this.addressService.getCountries().pipe(
    shareReplay(1),
  );

  public readonly selectAddressControl = this.fb.control(
    { disabled: false, value: null },
    [Validators.required],
  );

  public readonly countryChanges$ = defer(() => this.formGroup.get('country').valueChanges.pipe(
    startWith(this.formGroup.get('country').value),
  ));

  private parsePhoneNumber: typeof parsePhoneNumber;

  public get control(): FormControl {
    return this.ngControl?.control as FormControl;
  }

  ngOnInit(): void {
    super.ngOnInit();

    (window as any).PayeverStatic?.SvgIconsLoader?.loadIcons(
      ['geocoder-24'],
      null,
      this.customElementService.shadowRoot
    );

    const basePhoneValidatorRequired = this.isPhoneRequired ? [Validators.required] : [];
    if (this.paymentSettings) {
      basePhoneValidatorRequired.push(AddressValidators.phoneValid(
        this.isPhoneRequired,
        this.paymentSettings.codeRequired,
        this.paymentSettings.phonePattern,
        this.paymentSettings.phonePatternCodeRequired,
      ));
      this.formGroup.get('phone').setValidators(basePhoneValidatorRequired);
      this.formGroup.updateValueAndValidity();
    } else {
      this.loadLibPhoneNumber();
    }

    const updateZipCodeValidation$ = this.formGroup.get('country').valueChanges
    .pipe(
      startWith(this.formGroup.get('country').value),
      tap((value: string) => {
        this.formGroup.get('zipCode').setValidators([
          ZipCodeValidators.Required,
          ZipCodeValidators.zipCodeValid(this.paymentSettings?.postalCodePattern || POSTAL_PATTERNS_BY_COUNTRY[value]),
        ]);
        this.formGroup.get('zipCode').updateValueAndValidity();
      })
    );

    const updatePhoneValidation$ = this.formGroup.get('country').valueChanges.pipe(
      startWith(this.formGroup.get('country').value),
      tap((country) => {
        const codeRequired = this.isCodeForPhoneRequired;
        const controlName = $localize`:@@checkout_address_edit.form.phone.label:`;
        const validators = [
          PhoneValidators.country(this.paymentSettings?.countryCode || country, controlName),
          ...basePhoneValidatorRequired,
        ];
        codeRequired && validators.push(PhoneValidators.codeRequired(this.paymentSettings?.countryCode || country));
        this.formGroup.get('phone').setValidators(validators);
        this.formGroup.get('phone').updateValueAndValidity();
      })
    );

    const updateFullAddress$ = merge(
      this.formGroup.get('country').valueChanges.pipe(
        startWith(this.formGroup.get('country').value),
        map(country => ({ country })),
      ),
      this.formGroup.get('city').valueChanges.pipe(
        startWith(this.formGroup.get('city').value),
        map(city => ({ city })),
      ),
      this.formGroup.get('street').valueChanges.pipe(
        startWith(this.formGroup.get('street').value),
        map(street => ({ street })),
        tap(({ street }) => {
          const streetName = street?.split(/(\d+)/)[0]?.trim();
          const streetNumber = street?.split(/(\d+)/)[1]?.trim();

          this.formGroup.get('streetName').setValue(streetName);
          this.formGroup.get('streetNumber').setValue(streetNumber);
        }),
      ),
      this.formGroup.get('zipCode').valueChanges.pipe(
        startWith(this.formGroup.get('zipCode').value),
        map(zipCode => ({ zipCode })),
      ),
    ).pipe(
      scan((acc, curr) => ({ ...acc, ...curr }), {} as AddressInterface),
      debounceTime(100),
      switchMap(value => this.countriesOptions$.pipe(
        tap((countries) => {
          const fullAddress = this.addressService.parseFullAddress(value, countries);
          this.formGroup.get('fullAddress').patchValue(fullAddress, { emitEvent: false });
        }),
      )),
    );

    const selectAddressChange$ = this.selectAddressControl.valueChanges.pipe(
      tap((value: AddressFormInterface) => {
        const newValue = {
          city: value?.city || '',
          country: value?.country || '',
          firstName: value?.firstName || '',
          fullAddress: value?.fullAddress || '',
          lastName: value?.lastName || '',
          socialSecurityNumber: value?.socialSecurityNumber || '',
          street: value?.street || '',
          streetName: value?.streetName || '',
          streetNumber: value?.streetNumber || '',
          zipCode: value?.zipCode || '',
          ...(value === null && {
            email: '',
            phone: '',
            salutation: '',
          }),
        };

        this.formGroup.patchValue(newValue);
      }),
    );

    this.markFieldsAsReadonly();
    merge(
      updateZipCodeValidation$,
      updatePhoneValidation$,
      updateFullAddress$,
      selectAddressChange$,
    ).pipe(
      takeUntil(this.destroy$),
    ).subscribe();
  }

  public fullAddressChange(value: Partial<AddressInterface>): void {
    this.formGroup.patchValue(value);
  }

  private markFieldsAsReadonly(): void {
    this.isFilledFieldsReadonly && Object.values(this.formGroup.controls).forEach((control) => {
      if (control.value) {
        control.disable();
      }
    });
  }

  private loadLibPhoneNumber(): void {
    import('libphonenumber-js/max').then((m) => {
      this.parsePhoneNumber = m.parsePhoneNumber;
    });
  }
}
